import * as THREE from 'three'
import './style.css'
import { ArcadeVehicle, type DriveInput } from './vehicle.ts'
import {
  defaultVehicleCameraConfig,
  parseVehicleCameraIni,
  type VehicleCameraConfig,
} from './vehicle/camera-config.ts'
import { GamepadController, type GamepadDrivingInput } from './vehicle/gamepad-input.ts'
import {
  connectTelemetryBroadcast,
  downloadTelemetryNdjson,
  VehicleTelemetryBus,
  VehicleTelemetryRingRecorder,
  type VehicleTelemetryFrameV1,
} from './vehicle/telemetry.ts'
import { WorldStreamer } from '../../../packages/world-streamer/src/index.ts'

const DEFAULT_LOCATION = { latitude: -33.8795, longitude: 151.1035 }

const wheelDebugMarkup = ['FL', 'FR', 'RL', 'RR'].map((label, index) => `
  <article class="vehicle-wheel" data-wheel="${index}">
    <header><b>${label}</b><span data-field="contact">CONTACT</span></header>
    <div class="wheel-visual">
      <i class="wheel-slip-angle" data-field="slip-angle-line"></i>
      <span class="wheel-slip-ratio"><i data-field="slip-ratio-bar"></i></span>
    </div>
    <dl>
      <dt>Slip angle</dt><dd data-field="slip-angle">0.0°</dd>
      <dt>Slip ratio</dt><dd data-field="slip-ratio">0.000</dd>
      <dt>Load</dt><dd data-field="load">0 N</dd>
      <dt>Fx / Fy</dt><dd data-field="forces">0 / 0 N</dd>
      <dt>Grip use</dt><dd data-field="grip">0%</dd>
    </dl>
  </article>
`).join('')
const gTrailMarkup = Array.from({ length: 12 }, (_, index) => `<i class="g-trail" data-g-trail="${index}"></i>`).join('')

const app = document.querySelector<HTMLDivElement>('#app')!
app.innerHTML = `
  <canvas class="webgl" tabindex="0"></canvas>
  <aside class="debug-overlay" data-debug-page="0" hidden>
    <header>
      <span>F2 / <b id="debug-page-number">PAGE 01</b></span>
      <strong id="debug-page-title">ROAD SPLINES</strong>
    </header>
    <dl class="debug-coordinates">
      <dt>X / EAST</dt><dd id="debug-x">+0.00</dd>
      <dt>Y / UP</dt><dd id="debug-y">+0.00</dd>
      <dt>Z / SOUTH</dt><dd id="debug-z">+0.00</dd>
    </dl>
    <section class="debug-page debug-page-splines">
      <p class="debug-kicker">VEHICLE-CONTACT AUTHORITY</p>
      <div class="debug-legend debug-legend-grid">
        <span><i style="--swatch:#ff4d5f"></i>Highway-scale</span>
        <span><i style="--swatch:#ffad33"></i>Major</span>
        <span><i style="--swatch:#00e5ff"></i>Street</span>
        <span><i style="--swatch:#ad64ff"></i>Service-scale</span>
      </div>
      <div class="debug-legend debug-legend-grid debug-node-legend">
        <span><i style="--swatch:#f4f7ef"></i>Endpoint</span>
        <span><i style="--swatch:#ffd24d"></i>Through node</span>
        <span><i style="--swatch:#ff4d5f"></i>Junction</span>
        <span><i style="--swatch:#b57cff"></i>Chunk boundary</span>
      </div>
      <p class="debug-counts"><span id="debug-spline-count">0</span> spline spans · <span id="debug-node-count">0</span> nodes</p>
    </section>
    <section class="debug-page debug-page-wireframe" hidden>
      <p class="debug-kicker">EXACT RENDERED TRIANGLES</p>
      <div class="debug-legend">
        <span><i style="--swatch:#ffe14d"></i>Road ribbon</span>
        <span><i style="--swatch:#ff4dd2"></i>Junction fill</span>
      </div>
      <p class="debug-counts"><span id="debug-road-triangles">0</span> road tris · <span id="debug-junction-triangles">0</span> junction tris</p>
      <p class="debug-note">Wireframe draws through grass. Clean lines beneath green mean terrain overlap; pinched lines mean road topology.</p>
    </section>
    <section class="debug-page debug-page-vehicle" hidden>
      <div class="vehicle-debug-summary">
        <span><b id="debug-speed">0.0</b> km/h</span>
        <span>Gear <b id="debug-gear">1</b></span>
        <span><b id="debug-rpm">800</b> rpm</span>
        <span><b id="debug-grounded-wheels">4</b>/4 contact</span>
      </div>
      <div class="vehicle-debug-layout">
        <div class="vehicle-wheel-grid">
          ${wheelDebugMarkup}
        </div>
        <div class="vehicle-g-meter">
          <p>Lateral / longitudinal G</p>
          <div class="g-circle" id="debug-g-circle">
            ${gTrailMarkup}
            <b id="debug-g-dot"></b>
          </div>
          <span id="debug-g-value">0.00 / 0.00 G</span>
        </div>
      </div>
    </section>
    <footer>F2 / NEXT PAGE / OFF</footer>
  </aside>
  <div class="telemetry-status" hidden>F9 / TELEMETRY REC</div>
  <div class="source-credit">
    <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">© OpenStreetMap contributors</a>
    <span>·</span>
    <a href="https://registry.opendata.aws/terrain-tiles/" target="_blank" rel="noreferrer">Mapzen terrain</a>
  </div>
`

const canvas = document.querySelector<HTMLCanvasElement>('canvas.webgl')!
const debugOverlay = document.querySelector<HTMLElement>('.debug-overlay')!
const debugPageNumber = document.querySelector<HTMLElement>('#debug-page-number')!
const debugPageTitle = document.querySelector<HTMLElement>('#debug-page-title')!
const debugSplinePage = document.querySelector<HTMLElement>('.debug-page-splines')!
const debugWireframePage = document.querySelector<HTMLElement>('.debug-page-wireframe')!
const debugVehiclePage = document.querySelector<HTMLElement>('.debug-page-vehicle')!
const debugX = document.querySelector<HTMLElement>('#debug-x')!
const debugY = document.querySelector<HTMLElement>('#debug-y')!
const debugZ = document.querySelector<HTMLElement>('#debug-z')!
const debugSplineCount = document.querySelector<HTMLElement>('#debug-spline-count')!
const debugNodeCount = document.querySelector<HTMLElement>('#debug-node-count')!
const debugRoadTriangles = document.querySelector<HTMLElement>('#debug-road-triangles')!
const debugJunctionTriangles = document.querySelector<HTMLElement>('#debug-junction-triangles')!
const debugSpeed = document.querySelector<HTMLElement>('#debug-speed')!
const debugGear = document.querySelector<HTMLElement>('#debug-gear')!
const debugRpm = document.querySelector<HTMLElement>('#debug-rpm')!
const debugGroundedWheels = document.querySelector<HTMLElement>('#debug-grounded-wheels')!
const debugGDot = document.querySelector<HTMLElement>('#debug-g-dot')!
const debugGValue = document.querySelector<HTMLElement>('#debug-g-value')!
const debugGTrail = [...document.querySelectorAll<HTMLElement>('[data-g-trail]')]
const debugWheelCards = [...document.querySelectorAll<HTMLElement>('.vehicle-wheel')]
const telemetryStatus = document.querySelector<HTMLElement>('.telemetry-status')!
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' })
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5))
renderer.setSize(innerWidth, innerHeight)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.outputColorSpace = THREE.SRGBColorSpace
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.05

const scene = new THREE.Scene()
scene.background = new THREE.Color('#adcee0')
scene.fog = new THREE.Fog('#adcee0', 850, 2400)
const camera = new THREE.PerspectiveCamera(56, innerWidth / innerHeight, 0.08, 3600)
camera.position.set(8, 6, -10)

const hemisphere = new THREE.HemisphereLight('#e8f6ff', '#70735c', 2.25)
scene.add(hemisphere)
const sun = new THREE.DirectionalLight('#fff1d5', 3.2)
sun.position.set(-130, 220, -90)
sun.castShadow = true
sun.shadow.mapSize.set(2048, 2048)
sun.shadow.camera.left = -70
sun.shadow.camera.right = 70
sun.shadow.camera.top = 70
sun.shadow.camera.bottom = -70
sun.shadow.camera.near = 40
sun.shadow.camera.far = 520
sun.shadow.bias = -0.0003
sun.shadow.normalBias = 0.015
scene.add(sun, sun.target)

const streamer = new WorldStreamer({ scene })
const rendererReady = streamer.prepareRenderer(renderer, camera).catch(() => undefined)
const vehicle = new ArcadeVehicle()
vehicle.group.position.y = vehicle.groundOffsetM
scene.add(vehicle.group)
const vehicleModelReady = vehicle.loadModel().catch((error: unknown) => {
  console.error('[Daylight Vehicle] GT86 model failed to load.', error)
})

const fallbackGrass = new THREE.Mesh(
  new THREE.PlaneGeometry(6_000, 6_000),
  new THREE.MeshStandardMaterial({ color: '#9fb584', roughness: 1, flatShading: true }),
)
fallbackGrass.name = 'FALLBACK_GRASS'
fallbackGrass.rotation.x = -Math.PI * 0.5
fallbackGrass.receiveShadow = true
scene.add(fallbackGrass)

const keys = new Set<string>()
const gamepadController = new GamepadController()
const telemetryBus = new VehicleTelemetryBus()
const telemetryRecorder = new VehicleTelemetryRingRecorder(30, 60)
const telemetryBroadcast = connectTelemetryBroadcast(telemetryBus)
telemetryBus.subscribe((frame) => telemetryRecorder.capture(frame))
let running = true
let lastTime = performance.now()
let vehicleCameraConfig: VehicleCameraConfig = defaultVehicleCameraConfig
let vehicleCameraConfigSource = ''
let vehicleCameraConfigRevision = 0
let vehicleCameraConfigError = ''
let vehicleCameraReloadTimer: number | undefined
let cameraDynamicsInitialised = false
let cameraVerticalPosition = 0
let cameraVerticalVelocity = 0
let cameraPreviousVerticalTarget = 0
let cameraSmoothedSpeedHeight = 0
let cameraReverseArcSign = 1
let cameraFollowHeadingRadians = 0
let cameraSmoothedFollowHeadingRadians = 0
let cameraLookYawRadians = 0
let cameraLookPitchRadians = 0
let cameraLookPivotBlend = 0
let keyboardSteering = 0
let currentGamepadInput: GamepadDrivingInput = {
  connected: false,
  id: '',
  steer: 0,
  drive: 0,
  lookX: 0,
  lookY: 0,
  handbrake: false,
  resetPressed: false,
  startPressed: false,
  hapticsAvailable: false,
}
let teleportSequence = 0
let fallbackHeight = 0
let debugPage: 0 | 1 | 2 | 3 = 0
let lastDebugStatsRefresh = 0
let telemetrySequence = 0
let telemetryTimeSeconds = 0
let telemetryAccumulatorSeconds = 0
let latestTelemetry: VehicleTelemetryFrameV1 | undefined
const gTrailHistory: Array<{ x: number; y: number }> = []
const cameraTarget = new THREE.Vector3()
const cameraBodyDirection = new THREE.Vector3(0, 0, 1)
const cameraTravelDirection = new THREE.Vector3(0, 0, 1)
const cameraFollowDirection = new THREE.Vector3(0, 0, 1)
const cameraOrbitDirection = new THREE.Vector3(0, 0, 1)
const cameraChasePosition = new THREE.Vector3()
const cameraOrbitPosition = new THREE.Vector3()
const cameraChaseTarget = new THREE.Vector3()
const cameraOrbitTarget = new THREE.Vector3()
const previousCameraVelocity = new THREE.Vector3()
const desiredGforceTranslation = new THREE.Vector3()
const smoothedGforceTranslation = new THREE.Vector3()
const worldUp = new THREE.Vector3(0, 1, 0)
let lastSpawn: ReturnType<WorldStreamer['findSpawn']> = { x: 0, z: 0, heading: 0 }
let activeTeleport: { latitude: number; longitude: number; promise: Promise<void> } | undefined
let framePerformance = {
  worldMilliseconds: 0,
  renderMilliseconds: 0,
  totalMilliseconds: 0,
}

function moveTowards(current: number, target: number, maximumDelta: number) {
  if (Math.abs(target - current) <= maximumDelta) return target
  return current + Math.sign(target - current) * maximumDelta
}

function wrapAngle(angle: number) {
  return Math.atan2(Math.sin(angle), Math.cos(angle))
}

function smoothingFactor(delta: number, timeConstantSeconds: number) {
  return timeConstantSeconds <= 0 ? 1 : 1 - Math.exp(-delta / timeConstantSeconds)
}

function smoothstep(minimum: number, maximum: number, value: number) {
  const factor = THREE.MathUtils.clamp((value - minimum) / Math.max(maximum - minimum, 0.0001), 0, 1)
  return factor * factor * (3 - 2 * factor)
}

function cameraStickAxis(value: number, deadzone: number) {
  const magnitude = Math.abs(value)
  if (magnitude <= deadzone) return 0
  return Math.sign(value) * (magnitude - deadzone) / (1 - deadzone)
}

function applyVehicleCameraIni(text: string) {
  if (text === vehicleCameraConfigSource) return false
  vehicleCameraConfig = parseVehicleCameraIni(text, vehicleCameraConfig)
  vehicleCameraConfigSource = text
  vehicleCameraConfigRevision += 1
  vehicleCameraConfigError = ''
  console.info(`[Daylight Camera] Applied VEHICLE_CAMERA.ini revision ${vehicleCameraConfigRevision}`)
  return true
}

async function reloadVehicleCameraIni() {
  try {
    const response = await fetch(`/config/VEHICLE_CAMERA.ini?live=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) throw new Error(`Camera INI request failed with HTTP ${response.status}`)
    applyVehicleCameraIni(await response.text())
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    if (message !== vehicleCameraConfigError) {
      console.warn(`[Daylight Camera] ${message}; keeping revision ${vehicleCameraConfigRevision}`)
    }
    vehicleCameraConfigError = message
  }
}

function scheduleVehicleCameraReload() {
  if (vehicleCameraReloadTimer !== undefined) window.clearTimeout(vehicleCameraReloadTimer)
  if (!vehicleCameraConfig.liveReload.enabled) return
  vehicleCameraReloadTimer = window.setTimeout(async () => {
    await reloadVehicleCameraIni()
    scheduleVehicleCameraReload()
  }, vehicleCameraConfig.liveReload.pollIntervalMs)
}

function resetCameraDynamics() {
  cameraDynamicsInitialised = false
  cameraReverseArcSign = 1
  cameraLookYawRadians = 0
  cameraLookPitchRadians = 0
  cameraLookPivotBlend = 0
}

function driveInput(delta: number): DriveInput {
  const keyboardTarget = Number(keys.has('KeyA') || keys.has('ArrowLeft'))
    - Number(keys.has('KeyD') || keys.has('ArrowRight'))
  const speedBlend = THREE.MathUtils.smoothstep(Math.abs(vehicle.physics.longitudinalSpeed), 6, 35)
  const keyboardAuthority = THREE.MathUtils.lerp(1, 0.55, speedBlend)
  const keyboardRate = keyboardTarget === 0 ? 4.5 : THREE.MathUtils.lerp(3, 1.4, speedBlend)
  keyboardSteering = moveTowards(keyboardSteering, keyboardTarget * keyboardAuthority, keyboardRate * delta)
  const keyboardThrottle = keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0
  const keyboardBrake = keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0
  const gamepadThrottle = Math.max(0, currentGamepadInput.drive)
  const gamepadBrake = Math.max(0, -currentGamepadInput.drive)
  return {
    throttle: Math.max(keyboardThrottle, gamepadThrottle),
    brake: Math.max(keyboardBrake, gamepadBrake),
    steering: Math.abs(currentGamepadInput.steer) > Math.abs(keyboardSteering)
      ? currentGamepadInput.steer
      : keyboardSteering,
    handbrake: keys.has('Space') || currentGamepadInput.handbrake,
  }
}

function resetVehicle() {
  const referenceY = lastSpawn.sampleHeight === undefined ? undefined : lastSpawn.sampleHeight + vehicle.groundOffsetM
  vehicle.reset(lastSpawn.x, lastSpawn.z, lastSpawn.heading, (x, z) => streamer.sampleSurface(x, z, referenceY))
  resetCameraDynamics()
}

function recoverVehicle() {
  if (!vehicle.recover(sampleSurface)) resetVehicle()
  resetCameraDynamics()
}

function sampleSurface(x: number, z: number, referenceY?: number) {
  if (fallbackGrass.visible) return { height: fallbackHeight, grip: 0.52, road: false as const }
  return streamer.sampleSurface(x, z, referenceY)
}

function signedCoordinate(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`
}

function setDebugPage(page: 0 | 1 | 2 | 3): void {
  debugPage = page
  streamer.setDebugPage(page === 3 ? 0 : page)
  debugOverlay.hidden = page === 0
  debugOverlay.dataset.debugPage = String(page)
  debugSplinePage.hidden = page !== 1
  debugWireframePage.hidden = page !== 2
  debugVehiclePage.hidden = page !== 3
  if (page === 1) {
    debugPageNumber.textContent = 'PAGE 01'
    debugPageTitle.textContent = 'ROAD SPLINES + NODES'
  } else if (page === 2) {
    debugPageNumber.textContent = 'PAGE 02'
    debugPageTitle.textContent = 'ROAD MESH WIREFRAME'
  } else if (page === 3) {
    debugPageNumber.textContent = 'PAGE 03'
    debugPageTitle.textContent = 'VEHICLE PHYSICS'
  }
  lastDebugStatsRefresh = 0
}

function cycleDebugPage(): void {
  setDebugPage(((debugPage + 1) % 4) as 0 | 1 | 2 | 3)
}

function updateDebugOverlay(now: number): void {
  if (debugPage === 0) return
  debugX.textContent = signedCoordinate(vehicle.group.position.x)
  debugY.textContent = signedCoordinate(vehicle.group.position.y)
  debugZ.textContent = signedCoordinate(vehicle.group.position.z)
  if (now - lastDebugStatsRefresh < 160) return
  lastDebugStatsRefresh = now
  if (debugPage === 3) {
    debugSpeed.textContent = (Math.abs(vehicle.physics.longitudinalSpeed) * 3.6).toFixed(1)
    debugGear.textContent = String(vehicle.physics.gear)
    debugRpm.textContent = Math.round(vehicle.physics.engineRpm).toLocaleString()
    debugGroundedWheels.textContent = String(vehicle.ride.groundedWheels)
    for (let index = 0; index < debugWheelCards.length; index += 1) {
      const card = debugWheelCards[index]
      const wheel = vehicle.physics.wheels[index]
      if (!wheel) continue
      const field = (name: string) => card.querySelector<HTMLElement>(`[data-field="${name}"]`)!
      field('contact').textContent = wheel.contact ? 'CONTACT' : 'AIR'
      field('contact').dataset.active = String(wheel.contact)
      field('slip-angle').textContent = `${wheel.slipAngleDegrees.toFixed(1)}°`
      field('slip-ratio').textContent = wheel.slipRatio.toFixed(3)
      field('load').textContent = `${Math.round(wheel.normalLoadN).toLocaleString()} N`
      field('forces').textContent = `${Math.round(wheel.longitudinalForceN)} / ${Math.round(wheel.lateralForceN)} N`
      field('grip').textContent = `${Math.round(wheel.gripUsage * 100)}%`
      field('slip-angle-line').style.transform = `translate(-50%, -100%) rotate(${THREE.MathUtils.clamp(wheel.slipAngleDegrees, -60, 60)}deg)`
      const slipRatioBar = field('slip-ratio-bar')
      const normalizedSlip = THREE.MathUtils.clamp(wheel.slipRatio / vehicle.record.tyres.peakSlipRatio, -1.5, 1.5)
      slipRatioBar.style.left = `${50 + Math.min(0, normalizedSlip) * 33}%`
      slipRatioBar.style.width = `${Math.abs(normalizedSlip) * 33}%`
    }
    const lateralG = THREE.MathUtils.clamp(vehicle.physics.lateralAcceleration / 9.81, -2, 2)
    const longitudinalG = THREE.MathUtils.clamp(vehicle.physics.longitudinalAcceleration / 9.81, -2, 2)
    const point = { x: lateralG / 2, y: -longitudinalG / 2 }
    gTrailHistory.unshift(point)
    gTrailHistory.length = Math.min(gTrailHistory.length, debugGTrail.length)
    debugGDot.style.left = `${50 + point.x * 44}%`
    debugGDot.style.top = `${50 + point.y * 44}%`
    for (let index = 0; index < debugGTrail.length; index += 1) {
      const trail = gTrailHistory[index]
      debugGTrail[index].hidden = !trail
      if (!trail) continue
      debugGTrail[index].style.left = `${50 + trail.x * 44}%`
      debugGTrail[index].style.top = `${50 + trail.y * 44}%`
      debugGTrail[index].style.opacity = String((1 - index / debugGTrail.length) * 0.42)
    }
    debugGValue.textContent = `${lateralG.toFixed(2)} / ${longitudinalG.toFixed(2)} G`
    return
  }
  const stats = streamer.getDebugStats()
  debugSplineCount.textContent = stats.splineSegments.toLocaleString()
  debugNodeCount.textContent = stats.nodes.toLocaleString()
  debugRoadTriangles.textContent = stats.roadTriangles.toLocaleString()
  debugJunctionTriangles.textContent = stats.junctionTriangles.toLocaleString()
}

async function teleport(latitude: number, longitude: number): Promise<void> {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 85 || Math.abs(longitude) > 180) {
    throw new Error('Invalid world coordinate.')
  }
  if (activeTeleport && activeTeleport.latitude === latitude && activeTeleport.longitude === longitude) {
    return activeTeleport.promise
  }

  const sequence = ++teleportSequence
  fallbackHeight = vehicle.group.position.y - vehicle.groundOffsetM
  fallbackGrass.position.y = fallbackHeight
  fallbackGrass.visible = true
  framePerformance = { worldMilliseconds: 0, renderMilliseconds: 0, totalMilliseconds: 0 }
  const promise = (async () => {
    await rendererReady
    await vehicleModelReady
    await streamer.teleport(latitude, longitude)
    if (sequence !== teleportSequence) return
    lastSpawn = streamer.findSpawn()
    fallbackGrass.visible = false
    resetVehicle()
    canvas.focus()
  })()
  activeTeleport = { latitude, longitude, promise }
  try {
    await promise
  } finally {
    if (activeTeleport?.promise === promise) activeTeleport = undefined
  }
}

function updateCamera(delta: number) {
  const config = vehicleCameraConfig.chaseRoad
  const absoluteSpeed = Math.abs(vehicle.physics.longitudinalSpeed)
  cameraBodyDirection.set(Math.sin(vehicle.heading), 0, Math.cos(vehicle.heading)).normalize()
  const planarVelocitySquared = vehicle.physics.velocityX ** 2 + vehicle.physics.velocityZ ** 2
  if (planarVelocitySquared > 0.25) {
    cameraTravelDirection.set(vehicle.physics.velocityX, 0, vehicle.physics.velocityZ).normalize()
  } else cameraTravelDirection.copy(cameraBodyDirection)

  const bodyHeading = Math.atan2(cameraBodyDirection.x, cameraBodyDirection.z)
  const travelHeading = Math.atan2(cameraTravelDirection.x, cameraTravelDirection.z)
  const wrappedTravelDelta = wrapAngle(travelHeading - bodyHeading)
  const travelIsBehindCar = Math.cos(wrappedTravelDelta) < 0
  if (!travelIsBehindCar && Math.abs(wrappedTravelDelta) > 0.001) cameraReverseArcSign = Math.sign(wrappedTravelDelta)
  const continuousTravelDelta = travelIsBehindCar
    ? Math.abs(wrappedTravelDelta) * cameraReverseArcSign
    : wrappedTravelDelta
  const lowSpeedVelocityFrame = config.velocityFrame * smoothstep(0.25, 2, Math.sqrt(planarVelocitySquared))
  const desiredFollowHeading = bodyHeading + continuousTravelDelta * lowSpeedVelocityFrame
  if (!cameraDynamicsInitialised) cameraSmoothedFollowHeadingRadians = desiredFollowHeading
  else {
    cameraSmoothedFollowHeadingRadians += wrapAngle(desiredFollowHeading - cameraSmoothedFollowHeadingRadians)
      * smoothingFactor(delta, config.velocityFrameTransientDampingSeconds)
  }
  cameraFollowHeadingRadians = cameraSmoothedFollowHeadingRadians
  cameraFollowDirection.set(Math.sin(cameraFollowHeadingRadians), 0, Math.cos(cameraFollowHeadingRadians))

  const rawLookX = currentGamepadInput.lookX
  const rawLookY = currentGamepadInput.lookY
  const rawLookMagnitude = Math.min(1, Math.hypot(rawLookX, rawLookY))
  const lookMagnitude = Math.abs(cameraStickAxis(rawLookMagnitude, config.rightStickDeadzone))
  const radialLookYaw = rawLookMagnitude > 0 ? Math.atan2(-rawLookX, rawLookY) : 0
  const maximumLookYaw = THREE.MathUtils.degToRad(config.rightStickYawDegrees)
  const desiredLookYaw = THREE.MathUtils.clamp(radialLookYaw, -maximumLookYaw, maximumLookYaw) * lookMagnitude
  const desiredLookPitch = 0
  const activelyLooking = lookMagnitude > 0
  const lookResponse = activelyLooking ? config.rightStickResponse : config.rightStickReturnResponse
  const lookSmoothing = 1 - Math.exp(-lookResponse * delta)
  cameraLookYawRadians += wrapAngle(desiredLookYaw - cameraLookYawRadians) * lookSmoothing
  cameraLookPitchRadians += (desiredLookPitch - cameraLookPitchRadians) * lookSmoothing
  if (activelyLooking) cameraLookPivotBlend = 1
  else {
    cameraLookPivotBlend += (0 - cameraLookPivotBlend) * (1 - Math.exp(-config.rightStickReturnResponse * delta))
    if (cameraLookPivotBlend < 0.001) cameraLookPivotBlend = 0
  }
  cameraOrbitDirection.set(
    Math.sin(cameraFollowHeadingRadians + cameraLookYawRadians),
    0,
    Math.cos(cameraFollowHeadingRadians + cameraLookYawRadians),
  )

  const topSpeedFactor = THREE.MathUtils.clamp(
    absoluteSpeed * 3.6 / config.topSpeedVerticalPositionFullSpeedKph,
    0,
    1,
  )
  const desiredSpeedHeight = THREE.MathUtils.lerp(config.heightM, config.topSpeedVerticalPositionM, topSpeedFactor)
  if (!cameraDynamicsInitialised) {
    cameraDynamicsInitialised = true
    cameraSmoothedSpeedHeight = desiredSpeedHeight
    cameraVerticalPosition = vehicle.group.position.y + desiredSpeedHeight
    cameraVerticalVelocity = 0
    cameraPreviousVerticalTarget = cameraVerticalPosition
    previousCameraVelocity.set(vehicle.physics.velocityX, 0, vehicle.physics.velocityZ)
    smoothedGforceTranslation.set(0, 0, 0)
  }
  cameraSmoothedSpeedHeight = THREE.MathUtils.lerp(
    cameraSmoothedSpeedHeight,
    desiredSpeedHeight,
    smoothingFactor(delta, config.topSpeedVerticalPositionSmoothingSeconds),
  )

  const desiredVerticalPosition = vehicle.group.position.y + cameraSmoothedSpeedHeight
  const desiredVerticalVelocity = delta > 0 ? (desiredVerticalPosition - cameraPreviousVerticalTarget) / delta : 0
  cameraPreviousVerticalTarget = desiredVerticalPosition
  if (config.verticalPositionSpringRate === 0 && config.verticalPositionDamping === 0) {
    cameraVerticalPosition = desiredVerticalPosition
    cameraVerticalVelocity = 0
  } else {
    const verticalSteps = Math.max(1, Math.ceil(delta / (1 / 120)))
    const verticalDelta = delta / verticalSteps
    for (let index = 0; index < verticalSteps; index += 1) {
      const verticalAcceleration = config.verticalPositionSpringRate * (desiredVerticalPosition - cameraVerticalPosition)
        - config.verticalPositionDamping * (cameraVerticalVelocity - desiredVerticalVelocity)
      cameraVerticalVelocity += verticalAcceleration * verticalDelta
      cameraVerticalPosition += cameraVerticalVelocity * verticalDelta
    }
  }

  if (delta > 0) {
    const accelerationX = (vehicle.physics.velocityX - previousCameraVelocity.x) / delta
    const accelerationZ = (vehicle.physics.velocityZ - previousCameraVelocity.z) / delta
    const longitudinalAcceleration = accelerationX * cameraBodyDirection.x + accelerationZ * cameraBodyDirection.z
    // Longitudinal movement lags acceleration. Lateral movement goes toward
    // the corner, placing the car on the outside of the frame.
    desiredGforceTranslation.set(accelerationX, 0, accelerationZ)
      .addScaledVector(cameraBodyDirection, -2 * longitudinalAcceleration)
      .multiplyScalar(config.gforceTranslationMaxDistanceM / 9.81)
    if (desiredGforceTranslation.length() > config.gforceTranslationMaxDistanceM) {
      desiredGforceTranslation.setLength(config.gforceTranslationMaxDistanceM)
    }
    previousCameraVelocity.set(vehicle.physics.velocityX, 0, vehicle.physics.velocityZ)
  } else desiredGforceTranslation.set(0, 0, 0)
  smoothedGforceTranslation.lerp(
    desiredGforceTranslation,
    smoothingFactor(delta, config.gforceTranslationSmoothingSeconds),
  )

  const lookAheadFactor = THREE.MathUtils.clamp(absoluteSpeed / config.lookAheadFullSpeedMps, 0, 1)
  const lookAhead = THREE.MathUtils.lerp(config.lookAheadMinimumM, config.lookAheadMaximumM, lookAheadFactor)
  const horizontalBoomDistance = config.distanceM * Math.cos(cameraLookPitchRadians)
  cameraChasePosition.copy(vehicle.group.position)
    .addScaledVector(cameraFollowDirection, -config.distanceM)
    .add(smoothedGforceTranslation)
  cameraChasePosition.y = cameraVerticalPosition
  cameraOrbitPosition.copy(vehicle.group.position)
    .addScaledVector(cameraOrbitDirection, -horizontalBoomDistance)
    .add(smoothedGforceTranslation)
  cameraOrbitPosition.y = cameraVerticalPosition + Math.sin(cameraLookPitchRadians) * config.distanceM
  camera.position.lerpVectors(cameraChasePosition, cameraOrbitPosition, cameraLookPivotBlend)
  cameraChaseTarget.copy(vehicle.group.position)
    .addScaledVector(cameraFollowDirection, lookAhead)
    .addScaledVector(worldUp, config.targetHeightM)
  cameraOrbitTarget.copy(vehicle.group.position).addScaledVector(worldUp, config.targetHeightM)
  cameraTarget.lerpVectors(cameraChaseTarget, cameraOrbitTarget, cameraLookPivotBlend)
  camera.up.copy(worldUp)
  camera.lookAt(cameraTarget)

  const fovFactor = THREE.MathUtils.clamp(absoluteSpeed / config.fovFullSpeedMps, 0, 1)
  const desiredFov = THREE.MathUtils.lerp(config.baseFovDegrees, config.maximumFovDegrees, fovFactor)
  if (Math.abs(desiredFov - camera.fov) > 0.001) {
    camera.fov = desiredFov
    camera.updateProjectionMatrix()
  }
  sun.position.copy(vehicle.group.position).add(new THREE.Vector3(-130, 220, -90))
  sun.target.position.copy(vehicle.group.position)
}

function publishTelemetry(delta: number) {
  telemetryTimeSeconds += delta
  telemetryAccumulatorSeconds += delta
  if (telemetryAccumulatorSeconds < 1 / 60) return
  telemetryAccumulatorSeconds %= 1 / 60
  latestTelemetry = vehicle.createTelemetryFrame(++telemetrySequence, telemetryTimeSeconds)
  telemetryBus.publish(latestTelemetry)
}

function updateTelemetryStatus() {
  telemetryStatus.hidden = !telemetryRecorder.isRecording()
  if (!telemetryStatus.hidden) {
    telemetryStatus.textContent = `F9 / TELEMETRY REC · ${telemetryRecorder.size()} FRAMES · ${telemetryRecorder.durationSeconds().toFixed(1)} S / 30.0 S`
  }
}

function toggleTelemetryRecording() {
  if (!telemetryRecorder.isRecording()) {
    telemetryRecorder.start()
    updateTelemetryStatus()
    return
  }
  const frames = telemetryRecorder.stop()
  updateTelemetryStatus()
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  downloadTelemetryNdjson(frames, `daylight-club-${vehicle.record.id}-${timestamp}.telemetry.ndjson`)
}

function frame(now: number) {
  requestAnimationFrame(frame)
  const frameStartedAt = performance.now()
  const delta = Math.min((now - lastTime) / 1000, 0.05)
  lastTime = now
  currentGamepadInput = gamepadController.poll()
  if (currentGamepadInput.resetPressed && running) recoverVehicle()
  if (running) {
    const input = driveInput(delta)
    vehicle.update(
      delta,
      input,
      sampleSurface,
      (startX, startZ, endX, endZ, minY, maxY, radius) =>
        streamer.resolveStreamingMotion(startX, startZ, endX, endZ, minY, maxY, radius),
    )
    const planarSpeed = Math.hypot(vehicle.physics.velocityX, vehicle.physics.velocityZ)
    const streamDirectionX = planarSpeed > 0.25 ? vehicle.physics.velocityX / planarSpeed : Math.sin(vehicle.heading)
    const streamDirectionZ = planarSpeed > 0.25 ? vehicle.physics.velocityZ / planarSpeed : Math.cos(vehicle.heading)
    streamer.update(
      vehicle.group.position.x,
      vehicle.group.position.z,
      streamDirectionX,
      streamDirectionZ,
    )
    gamepadController.updateSlipHaptics(
      vehicle.physics.frontSlipAngleDegrees,
      vehicle.physics.rearSlipAngleDegrees,
      vehicle.record.tyres.peakSlipAngleDegrees,
      vehicle.physics.longitudinalSpeed,
      vehicle.physics.lateralSpeed,
      vehicle.surface,
      vehicle.physics.longitudinalAcceleration,
      input.throttle,
      input.brake,
      Math.max(vehicle.physics.wheels[0]?.gripUsage ?? 0, vehicle.physics.wheels[1]?.gripUsage ?? 0),
      Math.max(vehicle.physics.wheels[2]?.gripUsage ?? 0, vehicle.physics.wheels[3]?.gripUsage ?? 0),
    )
    publishTelemetry(delta)
  }
  streamer.pump()
  if (fallbackGrass.visible) {
    fallbackGrass.position.x = vehicle.group.position.x
    fallbackGrass.position.z = vehicle.group.position.z
  }
  const worldFinishedAt = performance.now()
  updateCamera(delta)
  updateDebugOverlay(now)
  updateTelemetryStatus()
  renderer.render(scene, camera)
  const renderFinishedAt = performance.now()
  framePerformance.worldMilliseconds = Math.max(framePerformance.worldMilliseconds, worldFinishedAt - frameStartedAt)
  framePerformance.renderMilliseconds = Math.max(framePerformance.renderMilliseconds, renderFinishedAt - worldFinishedAt)
  framePerformance.totalMilliseconds = Math.max(framePerformance.totalMilliseconds, renderFinishedAt - frameStartedAt)
}
requestAnimationFrame(frame)

window.addEventListener('keydown', (event: KeyboardEvent) => {
  keys.add(event.code)
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'F2', 'F9'].includes(event.code)) event.preventDefault()
  if (!event.repeat && event.code === 'F2') cycleDebugPage()
  if (!event.repeat && event.code === 'F9') toggleTelemetryRecording()
  if (!event.repeat && event.code === 'KeyR' && running) recoverVehicle()
})
window.addEventListener('keyup', (event: KeyboardEvent) => keys.delete(event.code))
window.addEventListener('blur', () => keys.clear())
window.addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(innerWidth, innerHeight)
  renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5))
})

declare global {
  interface Window {
    __DAYLIGHT_ALPHA__: {
      getState: () => Omit<ReturnType<WorldStreamer['getState']>, 'debugPage'> & {
        debugPage: 0 | 1 | 2 | 3
        speedKph: number
        roadName: string
        player: { x: number; y: number; z: number }
        grounded: boolean
        groundedWheels: number
        vehicleModelLoaded: boolean
        physicsModel: string
        telemetryBroadcastAvailable: boolean
        cameraConfigRevision: number
        cameraConfigError: string
        cameraDistanceM: number
        cameraActualDistanceM: number
        cameraHeightM: number
        cameraVerticalHeightM: number
        cameraFovDegrees: number
        cameraVelocityFrame: number
        cameraFollowHeadingRadians: number
        cameraLookYawDegrees: number
        cameraLookPivotBlend: number
        cameraGforceTranslationM: number
        cameraGforceTranslationMaximumM: number
        cameraGforceLateralM: number
        cameraUp: { x: number; y: number; z: number }
        terrainSourceValid: boolean
        framePerformance: typeof framePerformance
      }
      teleport: (lat: number, lon: number) => Promise<void>
      move: (x: number, z: number, heading?: number, referenceY?: number) => void
      probeSurfaceAuthorities: (x: number, z: number, referenceY?: number) => ReturnType<WorldStreamer['probeSurfaceAuthorities']>
      setDebugPage: (page: 0 | 1 | 2 | 3) => void
      findBoundaryRoadProbe: (fromMacroKey: string, toMacroKey: string) => ReturnType<WorldStreamer['findBoundaryRoadProbe']>
    }
    __DAYLIGHT_TELEMETRY__: {
      latest: () => VehicleTelemetryFrameV1 | undefined
      subscribe: (listener: (frame: VehicleTelemetryFrameV1) => void) => () => boolean
      startRecording: () => void
      stopRecording: () => readonly VehicleTelemetryFrameV1[]
    }
  }
}

window.__DAYLIGHT_ALPHA__ = {
  getState: () => ({
    ...streamer.getState(),
    debugPage,
    speedKph: Math.abs(vehicle.speed) * 3.6,
    roadName: vehicle.roadName,
    player: { x: vehicle.group.position.x, y: vehicle.group.position.y, z: vehicle.group.position.z },
    grounded: vehicle.grounded,
    groundedWheels: vehicle.ride.groundedWheels,
    vehicleModelLoaded: vehicle.modelLoaded,
    physicsModel: vehicle.record.physicsModel,
    telemetryBroadcastAvailable: telemetryBroadcast.available,
    cameraConfigRevision: vehicleCameraConfigRevision,
    cameraConfigError: vehicleCameraConfigError,
    cameraDistanceM: vehicleCameraConfig.chaseRoad.distanceM,
    cameraActualDistanceM: camera.position.distanceTo(vehicle.group.position),
    cameraHeightM: vehicleCameraConfig.chaseRoad.heightM,
    cameraVerticalHeightM: camera.position.y - vehicle.group.position.y,
    cameraFovDegrees: camera.fov,
    cameraVelocityFrame: vehicleCameraConfig.chaseRoad.velocityFrame,
    cameraFollowHeadingRadians,
    cameraLookYawDegrees: THREE.MathUtils.radToDeg(cameraLookYawRadians),
    cameraLookPivotBlend,
    cameraGforceTranslationM: smoothedGforceTranslation.length(),
    cameraGforceTranslationMaximumM: vehicleCameraConfig.chaseRoad.gforceTranslationMaxDistanceM,
    cameraGforceLateralM: -smoothedGforceTranslation.x * cameraBodyDirection.z
      + smoothedGforceTranslation.z * cameraBodyDirection.x,
    cameraUp: { x: camera.up.x, y: camera.up.y, z: camera.up.z },
    terrainSourceValid: streamer.isTerrainSourceValid(vehicle.group.position.x, vehicle.group.position.z),
    framePerformance: { ...framePerformance },
  }),
  teleport,
  move: (x, z, heading, referenceY) => {
    vehicle.reset(x, z, heading ?? vehicle.heading, (sampleX, sampleZ) => streamer.sampleSurface(sampleX, sampleZ, referenceY))
    resetCameraDynamics()
  },
  probeSurfaceAuthorities: (x, z, referenceY) => streamer.probeSurfaceAuthorities(x, z, referenceY),
  setDebugPage,
  findBoundaryRoadProbe: (fromMacroKey, toMacroKey) => streamer.findBoundaryRoadProbe(fromMacroKey, toMacroKey),
}

window.__DAYLIGHT_TELEMETRY__ = {
  latest: () => latestTelemetry,
  subscribe: (listener) => telemetryBus.subscribe(listener),
  startRecording: () => { telemetryRecorder.start(); updateTelemetryStatus() },
  stopRecording: () => { const frames = telemetryRecorder.stop(); updateTelemetryStatus(); return frames },
}

void reloadVehicleCameraIni().finally(scheduleVehicleCameraReload)
void teleport(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude).catch(() => undefined)
